import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { getCustomer, listCustomers } from '../Api/Customer/CustomerApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';

export interface ChildCustomer {
  id: string;
  name: string;
}

export interface Customer {
  id: string;
  sf_id: string;
  name: string;
  segment: string;
  salesAgent: string | null;
  parentCustomer: string | null;
  parentCustomerId: string | null;
  ultimateParent: string | null;
  ultimateParentId: string | null;
  customerSuccessManager: string | null;
  commissionsExperienceAnalyst: string | null;
  billingAddress: string | null;
  shippingAddress: string | null;
  assetCount: string | null;
  rpmAccountCount: string | null;
  childCustomers: ChildCustomer[] | null;
  childCustomersCount: string | null;
  paCount: string | null;
  assetBncCount: string | null;
  assetCommissioningCount: string | null;
  suppliers?: { id: string; name: string }[];
}

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const customerSubject = new BehaviorSubject<Customer>({} as Customer);
const cruxCustomersSubject = new BehaviorSubject<PaginatedResponse<Customer>>({} as PaginatedResponse<Customer>);
const customerLoadingSubject = new BehaviorSubject<boolean>(false);
const cruxCustomersLoadingSubject = new BehaviorSubject<boolean>(false);

const clearError = () => addError('customer', undefined);

export const getCustomerSubject = (): BehaviorSubject<Customer> => customerSubject;
export const getCruxCustomersSubject = (): BehaviorSubject<PaginatedResponse<Customer>> => cruxCustomersSubject;
export const getCruxCustomersLoadingSubject = (): BehaviorSubject<boolean> => cruxCustomersLoadingSubject;
export const getCustomerLoadingSubject = (): BehaviorSubject<boolean> => customerLoadingSubject;

export const getCustomerById = async (id: string) => {
  const cust = cruxCustomersSubject?.getValue()?.data?.find((cus) => cus?.id === id);
  if (cust) {
    customerSubject.next(cust);
    return;
  }

  if (!cust) {
    return await fetchCustomer(id);
  }
};

export const fetchCustomer = async (id: string) => {
  customerLoadingSubject.next(true);

  const { data, error } = await getCustomer(id);

  if (data) {
    clearError();
    customerSubject.next(data);
  }
  if (error) {
    addError('customer', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }

  customerLoadingSubject.next(false);
};

export const fetchCustomers = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (cruxCustomersSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  cruxCustomersLoadingSubject.next(true);

  const { data, error } = await listCustomers(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    cruxCustomersSubject.next(data);
  }
  if (error) addError('customer', error.message);

  newFetchNumber === currentFetchNumber && cruxCustomersLoadingSubject.next(false);
};
